import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Loader } from '@onramp/components/Loader';
import { PageWrapper } from '@onramp/components/PageWrapper';
import { useSplashScreenContext } from '@onramp/components/SplashScreen/context';
import type {
  PolicyRestrictionsGuardFragment$data,
  PolicyRestrictionsGuardFragment$key,
} from '@onramp/data/__generated__/PolicyRestrictionsGuardFragment.graphql';
import type { PolicyRestrictionsGuardQuery } from '@onramp/data/__generated__/PolicyRestrictionsGuardQuery.graphql';
import { useIsWalletApp } from '@onramp/hooks/useIsWalletApp';
import { useReportAndViewError } from '@onramp/hooks/useReportAndViewError';
import useInterval from '@onramp/hooks/utils/useInterval';
import { useOpenUrl } from '@onramp/hooks/utils/useOpenUrl';
import type { WidgetKey } from '@onramp/shared/appParams.schema';
import { getMobileAppBundleId, getMobileAppVersion, isCBWalletApp } from '@onramp/utils/appVersion';
import { setBugsnagUserDetails } from '@onramp/utils/bugsnag';
import { clientSessionIdStore } from '@onramp/utils/clientSessionIdStore';
import copyToClipboard from '@onramp/utils/copyToClipboard';
import { isTestEnvironment } from '@onramp/utils/environment/sharedEnv';
import { HandledError, reportError } from '@onramp/utils/errors';
import { logEvent } from '@onramp/utils/eventing/logEvent';
import { getIsCBWalletAppWithPolicyRestrictionSupport } from '@onramp/utils/getIsCBWalletAppWithPolicyRestrictionSupport';
import { useIsCountryAllowed } from '@onramp/utils/getIsCountryAllowed';
import { identifyUser } from '@onramp/utils/init/analytics';
import { broadcastEvent, isMobileExperience } from '@onramp/utils/postMessage';
import { assertPresence } from '@onramp/utils/relayUtils';
import { isNotNullish } from '@onramp/utils/types';
import { getErrorVitals } from '@onramp/v2/client/utils/initErrorVitals';
import semver from 'semver';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { HeroSquare } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Link, TextBody, TextLabel1, TextTitle3 } from '@cbhq/cds-web/typography';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import { graphql, useLazyLoadQuery, useRefetchableFragment } from '@cbhq/data-layer';

import { useAppParams } from '../AppManagerProvider';

import { BlockedUkUserOverlay } from './BlockedUkUserOverlay';
import { policyRestrictionsGuardMessages as messages } from './messages';
import { WebView2FaRestrictionOverlay } from './WebView2faRestrictionOverlay';

type PolicyRestrictionsConfig = {
  // Buy & Sends restrictions are from Retail that check the user's ability to buy/send.
  sends?: boolean;
  buys?: boolean;
};

export const policyRestrictionsConfigForWidget: Record<WidgetKey, PolicyRestrictionsConfig> = {
  buy: { sends: true, buys: true },
};

type PolicyRestrictionsGuardProps = {
  children: ReactNode;
};

const query = graphql`
  query PolicyRestrictionsGuardQuery($sends: Boolean!, $buys: Boolean!, $inWebView: Boolean!) {
    viewer @required(action: THROW) {
      ...PolicyRestrictionsGuardFragment
        @arguments(sends: $sends, buys: $buys, inWebView: $inWebView)
    }
  }
`;

const policyRestrictionsGuardFragment = graphql`
  fragment PolicyRestrictionsGuardFragment on Viewer
  @refetchable(queryName: "PolicyRestrictionsGuardRefetchableFragment")
  @argumentDefinitions(
    sends: { type: "Boolean!" }
    buys: { type: "Boolean!" }
    inWebView: { type: "Boolean!" }
  ) {
    userProperties {
      uuid
      country {
        code
        name
      }
    }

    isWebview2faEligible @include(if: $inWebView)

    sendRestrictions: policyRestrictions(action: sends) @include(if: $sends) {
      restrictionName
      link {
        url
        text
      }
      description
      message
    }

    buyRestrictions: policyRestrictions(action: buys) @include(if: $buys) {
      restrictionName
      link {
        url
        text
      }
      description
      message
    }
  }
`;

const CountryNotAllowedMessage: React.FC<{ country: string }> = ({ country }) => {
  const { formatMessage } = useIntl();
  return (
    <TextBody as="p" align="center" color="foregroundMuted">
      {formatMessage(messages.countryNotAllowedMessage, {
        country,
        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        link: <Link href="https://www.coinbase.com">Coinbase</Link>,
      })}
    </TextBody>
  );
};

function useShowUpdateWalletAppErrorMessageIfNeeded() {
  const isFaultyVersion = useMemo(() => {
    if (isTestEnvironment || !isMobileExperience() || !isCBWalletApp()) return false;

    const versionString = getMobileAppVersion();

    if (!versionString) return true;

    return semver.lt(versionString, '28.16.0');
  }, []);

  const isWalletAppId = useIsWalletApp();

  const shouldShowUpdateWalletAppErrorMessage = useMemo(
    () => isFaultyVersion && isWalletAppId,
    [isFaultyVersion, isWalletAppId],
  );
  const reportAndViewError = useReportAndViewError();

  useEffect(() => {
    const isReturn3DSRoute =
      window.location.pathname.includes('/return-3ds') ||
      window.location.search.includes('nonce3ds=');

    if (shouldShowUpdateWalletAppErrorMessage && !isReturn3DSRoute) {
      reportAndViewError(
        new HandledError({
          message: 'Please update your Base app to buy crypto',
          debugMessage: 'Blocking CBWallet app user from version <= 28.16.0',
        }).addMetadata({ bundleId: getMobileAppBundleId(), appVersion: getMobileAppVersion() }),
      );
    }
  }, [shouldShowUpdateWalletAppErrorMessage, reportAndViewError]);
}

export const PolicyRestrictionsGuard = ({ children }: PolicyRestrictionsGuardProps) => {
  const { formatMessage } = useIntl();
  const [hasClearedRestrictionsCheck, setHasClearedRestrictionsCheck] = useState(false);
  const [showClose, setShowClose] = useState(false);

  const handleClose = useCallback(() => broadcastEvent({ eventName: 'exit' }), []);

  const markAsCleared = useCallback(() => {
    setHasClearedRestrictionsCheck(true);
  }, []);

  useShowUpdateWalletAppErrorMessageIfNeeded();

  return hasClearedRestrictionsCheck ? (
    <>{children}</>
  ) : (
    <PageWrapper
      headerText={formatMessage(messages.title)}
      loadingElement={<Loader subText={formatMessage(messages.loading)} />}
      onBack={showClose ? handleClose : undefined}
    >
      <PolicyRestrictionsGuardCheck markAsCleared={markAsCleared} setShowClose={setShowClose} />
    </PageWrapper>
  );
};

type PolicyRestrictionsGuardCheckProps = {
  markAsCleared: () => void;
  setShowClose: (show: boolean) => void;
};

export const usePolicyRestrictionsGuardArgs = () => {
  const { formatMessage } = useIntl();
  const { activeWidget } = useAppParams();
  const widgetKey = assertPresence(activeWidget, {
    debugMessage: 'Widget key is not defined',
    message: formatMessage(messages.failedToLoadAccountInfo),
  });
  return policyRestrictionsConfigForWidget[widgetKey];
};

const PolicyRestrictionsGuardCheck = ({
  markAsCleared,
  setShowClose,
}: PolicyRestrictionsGuardCheckProps) => {
  const { formatMessage } = useIntl();
  const { sends: isSendsRequired, buys: isBuysRequired } = usePolicyRestrictionsGuardArgs();

  const splashScreenContext = useSplashScreenContext();

  const { viewer } = useLazyLoadQuery<PolicyRestrictionsGuardQuery>(query, {
    sends: isSendsRequired || false,
    buys: isBuysRequired || false,
    inWebView: isMobileExperience(),
  });
  const [data, refetch] = useRefetchableFragment(
    policyRestrictionsGuardFragment,
    viewer as PolicyRestrictionsGuardFragment$key,
  );
  useSetUserProperties(data);

  useInterval(() => {
    refetch({}, { fetchPolicy: 'network-only' });
  }, 30000);

  const sendRestrictions = assertPresence(isSendsRequired ? data.sendRestrictions : [], {
    debugMessage: 'Failed to load send restrictions',
    message: formatMessage(messages.failedToLoadAccountInfo),
    mockEnvVar: 'SEND_RESTRICTIONS',
  });
  const buyRestrictions = assertPresence(isBuysRequired ? data.buyRestrictions : [], {
    debugMessage: 'Failed to load buy restrictions.',
    message: formatMessage(messages.failedToLoadAccountInfo),
    mockEnvVar: 'BUY_RESTRICTIONS',
  });

  const countryCode = data.userProperties.country?.code;
  const countryName = data.userProperties.country?.name || '';
  const isCountryAllowed = useIsCountryAllowed(countryCode);
  const reportAndViewError = useReportAndViewError();
  const isWalletApp = useIsWalletApp();

  const isUkFcaApproved = clientSessionIdStore.getFeatureFlags()?.ukFcaApproved ?? false;

  const ukUserBlocked = useMemo(
    () => countryCode === 'GB' && !isUkFcaApproved,
    [countryCode, isUkFcaApproved],
  );

  const webview2faBlocked = useMemo(
    () => isMobileExperience() && !data.isWebview2faEligible,
    [data.isWebview2faEligible],
  );

  const userRestrictions = useMemo(() => {
    const restrictions = [...sendRestrictions, ...buyRestrictions];

    const restriction = restrictions.filter(isNotNullish).filter(
      // Only exclude 'payment_method_required' restriction
      (res) => res.restrictionName !== 'payment_method_required',
    );

    return restriction;
  }, [sendRestrictions, buyRestrictions]);
  const userRestriction = useMemo(() => userRestrictions[0], [userRestrictions]);

  // TODO [ONRAMP-2034]: Remove this hotfix once longterm fix is in place
  const indirectLinkHotfix = useMemo(() => isWalletApp && isMobileExperience(), [isWalletApp]);

  useEffect(() => {
    if (!ukUserBlocked && !userRestriction && !webview2faBlocked) {
      markAsCleared();
    }

    if (ukUserBlocked) {
      logEvent('onramp.blocked_by_uk_fca', {
        action: ActionType.process,
        componentType: ComponentType.unknown,
      });
      reportError(new HandledError({ message: 'UK user blocked by FCA restrictions' }), {
        broadcast: false,
      });
    } else if (webview2faBlocked) {
      logEvent('onramp.blocked_by_webview_2fa_guard', {
        action: ActionType.process,
        componentType: ComponentType.unknown,
      });
      reportError(
        new HandledError({ message: 'Web View users is blocked due to unsupported 2FA methods' }),
        {
          broadcast: false,
        },
      );
    } else if (userRestrictions.length > 0) {
      const haveSendRestrictions = sendRestrictions.length > 0;
      const haveBuyRestrictions = buyRestrictions.length > 0;
      const sendRestrictionNames = sendRestrictions.map(
        (restriction) => restriction?.restrictionName,
      );
      const buyRestrictionNames = buyRestrictions.map(
        (restriction) => restriction?.restrictionName,
      );
      const restrictionNames = userRestrictions.map((restriction) => restriction?.restrictionName);

      reportError(
        new HandledError({ message: 'User is blocked by policy restrictions' }).addMetadata({
          restrictions: restrictionNames,
        }),
        { broadcast: false },
      );
      logEvent('blocked_by_policy_restrictions', {
        action: ActionType.process,
        componentType: ComponentType.unknown,
        loggingId: '465f4638-ae2d-4e4a-b111-69242959855b',
        restrictions: restrictionNames,
        haveSendRestrictions,
        haveBuyRestrictions,
        sendRestrictionNames,
        buyRestrictionNames,
      });

      if (indirectLinkHotfix) {
        setShowClose(true);
      }
    } else {
      setShowClose(false);
    }

    if (userRestriction && !userRestriction.link.url) {
      logEvent('onramp.blocked_by_policy_restriction_with_no_url', {
        action: ActionType.process,
        componentType: ComponentType.unknown,
        restrictionName: userRestriction.restrictionName,
        restrictionDescription: userRestriction.description,
        restritionMessage: userRestriction.message,
      });
    }

    if (!isCountryAllowed) {
      logEvent('onramp.unsupported_country', {
        action: ActionType.process,
        componentType: ComponentType.unknown,
        loggingId: 'a4cc3391-422f-4ffa-b9e7-5e9518e60eef',
        countryCode: countryCode ?? '<missing country>',
      });
      reportAndViewError(
        new HandledError({
          // Rich message overrides this so setting this to constant message
          message: `Coinbase Onramp is currently not available`,
        })
          .addHandlingParams({
            title: formatMessage(messages.notYetAvailable),
            richMessage: <CountryNotAllowedMessage country={countryName} />,
            pageTitle: 'Coinbase Onramp',
            backType: 'none',
            pictogramName: 'hero:restrictedCountry',
          })
          .addMetadata({
            countryCode,
            countryName,
          }),
      );
    }
  }, [
    countryCode,
    countryName,
    isCountryAllowed,
    markAsCleared,
    reportAndViewError,
    userRestriction,
    userRestrictions,
    formatMessage,
    setShowClose,
    indirectLinkHotfix,
    webview2faBlocked,
    ukUserBlocked,
    sendRestrictions,
    buyRestrictions,
  ]);

  if ((ukUserBlocked || webview2faBlocked || userRestriction) && isCountryAllowed) {
    if (splashScreenContext.isVisible) {
      splashScreenContext.dismissSplashScreen();
    }

    if (ukUserBlocked) {
      return <BlockedUkUserOverlay />;
    }

    if (webview2faBlocked) {
      return <WebView2FaRestrictionOverlay />;
    }

    return (
      <PolicyRestrictionsOverlay
        url={new URL(
          userRestriction.link.url || '/',
          process.env.NEXT_PUBLIC_COINBASE_BASE_URL,
        ).toString()}
        description={userRestriction.description}
        message={userRestriction.message}
        buttonText={userRestriction.link.text || formatMessage(messages.goToAccount)}
        indirectLinkHotfix={indirectLinkHotfix}
      />
    );
  }

  return null;
};

type PolicyRestrictionsOverlayProps = {
  url: string;
  description: string;
  message: string;
  buttonText: string;
  indirectLinkHotfix: boolean;
};

export const PolicyRestrictionsOverlay = ({
  url,
  description,
  message,
  buttonText,
  indirectLinkHotfix,
}: PolicyRestrictionsOverlayProps) => {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const isCBWalletAppWithPolicyRestrictionSupport = getIsCBWalletAppWithPolicyRestrictionSupport();

  const openUrl = useOpenUrl();

  const showPolicyRestrictionButton = useMemo(
    () => !indirectLinkHotfix || isCBWalletAppWithPolicyRestrictionSupport,
    [indirectLinkHotfix, isCBWalletAppWithPolicyRestrictionSupport],
  );

  const copyUrlToClipboard = useCallback(() => {
    copyToClipboard(url);
    toast.show(formatMessage(messages.copiedToClipboard), {
      hideCloseButton: true,
    });
  }, [formatMessage, toast, url]);

  const fullDescription = useMemo(() => {
    if (indirectLinkHotfix && !isCBWalletAppWithPolicyRestrictionSupport) {
      return `${description} ${formatMessage(messages.copyToContinue)}`;
    }

    return description;
  }, [description, formatMessage, indirectLinkHotfix, isCBWalletAppWithPolicyRestrictionSupport]);

  const handleButtonPress = useCallback(() => url && openUrl(url), [openUrl, url]);

  return (
    <VStack flexGrow={1} testID="policy-restrictions-overlay">
      <Box spacing={3} justifyContent="center">
        <HeroSquare name="securityShield" />
      </Box>
      <VStack spacing={1} spacingTop={0} flexGrow={1}>
        <TextTitle3 as="h3" align="center" spacingBottom={1}>
          {message}
        </TextTitle3>
        <TextBody as="p" align="center" color="foregroundMuted">
          {fullDescription}
        </TextBody>
        {indirectLinkHotfix && !isCBWalletAppWithPolicyRestrictionSupport && (
          <HStack alignItems="center" justifyContent="center" gap={2} spacing={2} flexGrow={1}>
            <TextLabel1 as="code">{url}</TextLabel1>
            <IconButton
              name="copy"
              variant="secondary"
              transparent
              onPress={copyUrlToClipboard}
              testID="policy-restrictions-copy-button"
            />
          </HStack>
        )}
      </VStack>
      {showPolicyRestrictionButton && (
        <Box spacing={3}>
          <Button block onPress={handleButtonPress} testID="policy-restrictions-link-button">
            {buttonText}
          </Button>
        </Box>
      )}
    </VStack>
  );
};

/**
 * Setting user properties for bugsnag & analytics
 */
const useSetUserProperties = (data: PolicyRestrictionsGuardFragment$data) => {
  const { formatMessage } = useIntl();
  const { userProperties } = data;
  const userUuid = userProperties.uuid || '';
  const countryCode = assertPresence(userProperties.country?.code, {
    debugMessage: 'Country code is not defined',
    message: formatMessage(messages.failedToLoadAccountInfo),
  });

  useEffect(() => {
    const walletUserId = clientSessionIdStore.getWalletAnalyticsData()?.walletUserId;
    const uuid = walletUserId ?? userUuid;

    if (uuid) {
      setBugsnagUserDetails(uuid);
      getErrorVitals().globalMetadata.userUuid = uuid;
      identifyUser({ userId: uuid, countryCode });
      clientSessionIdStore.setUserUuid(uuid);
    }
  }, [countryCode, userUuid]);
};
